// src/utils/migration.service.ts
import { storageService } from '../services/frontend-bundler/storage.service';
import { encryptionService } from '../services/frontend-bundler/encryption.service';
import { bundlerService } from '../services/frontend-bundler/bundler.service';

/**
 * Migration utilities for transitioning from backend to frontend-only architecture
 */
export class MigrationService {
  /**
   * Check if migration is needed
   */
  static async checkMigrationStatus(): Promise<{
    needsMigration: boolean;
    hasLegacyData: boolean;
    hasBackendConnection: boolean;
  }> {
    // Check for legacy localStorage data
    const hasLegacyData = !!(
      localStorage.getItem('bundler_wallets') ||
      localStorage.getItem('current_bundle_id') ||
      localStorage.getItem('wallet_bundles')
    );

    // Check if already migrated
    await storageService.initialize();
    const migrationComplete = await storageService.getSetting('migration_complete');

    // Check backend connection (optional)
    let hasBackendConnection = false;
    try {
      const response = await fetch('/api/health', { 
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      hasBackendConnection = response.ok;
    } catch {
      hasBackendConnection = false;
    }

    return {
      needsMigration: hasLegacyData && !migrationComplete,
      hasLegacyData,
      hasBackendConnection
    };
  }

  /**
   * Migrate from localStorage to IndexedDB
   */
  static async migrateLocalStorage(password: string): Promise<{
    success: boolean;
    bundlesMigrated: number;
    walletsMigrated: number;
    error?: string;
  }> {
    try {
      await storageService.initialize();
      
      let bundlesMigrated = 0;
      let walletsMigrated = 0;

      // Migrate bundler wallets
      const bundlerWalletsJson = localStorage.getItem('bundler_wallets');
      const currentBundleId = localStorage.getItem('current_bundle_id');

      if (bundlerWalletsJson) {
        const wallets = JSON.parse(bundlerWalletsJson);
        
        // Create encrypted wallets
        const encryptedWallets = [];
        for (const wallet of wallets) {
          // Encrypt the private key
          let encryptedPrivateKey;
          if (wallet.privateKey) {
            encryptedPrivateKey = await encryptionService.encrypt(
              wallet.privateKey,
              password
            );
          } else {
            // Generate a new keypair if private key is missing
            continue;
          }

          encryptedWallets.push({
            id: `migrated-wallet-${Date.now()}-${Math.random()}`,
            address: wallet.address,
            encryptedPrivateKey,
            balance: wallet.balance || 0,
            funded: wallet.funded || false,
            active: wallet.active !== false,
            createdAt: new Date()
          });
          walletsMigrated++;
        }

        // Create a bundle from migrated data
        const bundle = {
          id: currentBundleId || `migrated-bundle-${Date.now()}`,
          name: 'Migrated Bundle',
          owner: localStorage.getItem('wallet_address') || 'unknown',
          wallets: encryptedWallets,
          totalBalance: encryptedWallets.reduce((sum, w) => sum + w.balance, 0),
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
          settings: {
            autoRebalance: false,
            maxSlippage: 5,
            priorityFee: 0.0001,
            distributeEvenly: true,
            stealthMode: false
          }
        };

        await storageService.saveBundle(bundle);
        bundlesMigrated++;
      }

      // Mark migration as complete
      await storageService.saveSetting('migration_complete', true);
      await storageService.saveSetting('migration_date', new Date().toISOString());

      // Optionally backup old data before clearing
      const backup = {
        bundler_wallets: localStorage.getItem('bundler_wallets'),
        current_bundle_id: localStorage.getItem('current_bundle_id'),
        wallet_bundles: localStorage.getItem('wallet_bundles'),
        backupDate: new Date().toISOString()
      };
      await storageService.saveSetting('legacy_backup', backup);

      return {
        success: true,
        bundlesMigrated,
        walletsMigrated
      };
    } catch (error: any) {
      console.error('Migration error:', error);
      return {
        success: false,
        bundlesMigrated: 0,
        walletsMigrated: 0,
        error: error.message
      };
    }
  }

  /**
   * Export data from backend (if available)
   */
  static async exportFromBackend(): Promise<any> {
    try {
      const response = await fetch('/api/bundles/export-all', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export from backend');
      }

      return await response.json();
    } catch (error) {
      console.error('Backend export error:', error);
      return null;
    }
  }

  /**
   * Complete migration process
   */
  static async performFullMigration(
    password: string,
    onProgress?: (step: string, progress: number) => void
  ): Promise<{
    success: boolean;
    message: string;
    details: any;
  }> {
    try {
      const results = {
        localStorageMigration: null as any,
        backendMigration: null as any,
        cleanup: false
      };

      // Step 1: Check migration status
      onProgress?.('Checking migration status', 10);
      const status = await this.checkMigrationStatus();

      if (!status.needsMigration) {
        return {
          success: true,
          message: 'No migration needed',
          details: status
        };
      }

      // Step 2: Migrate localStorage data
      if (status.hasLegacyData) {
        onProgress?.('Migrating local storage', 30);
        results.localStorageMigration = await this.migrateLocalStorage(password);
      }

      // Step 3: Export from backend if available
      if (status.hasBackendConnection) {
        onProgress?.('Exporting from backend', 50);
        const backendData = await this.exportFromBackend();
        
        if (backendData) {
          onProgress?.('Importing backend data', 70);
          // Import backend bundles
          for (const bundle of backendData.bundles || []) {
            try {
              await bundlerService.importBundle(
                JSON.stringify(bundle),
                password
              );
            } catch (error) {
              console.error('Failed to import bundle:', error);
            }
          }
          results.backendMigration = {
            success: true,
            bundlesImported: backendData.bundles?.length || 0
          };
        }
      }

      // Step 4: Cleanup old data (optional)
      onProgress?.('Cleaning up', 90);
      if (results.localStorageMigration?.success) {
        // Clear old localStorage data after successful migration
        const keysToRemove = [
          'bundler_wallets',
          'current_bundle_id',
          'wallet_bundles',
          'bundle_settings'
        ];
        
        keysToRemove.forEach(key => {
          localStorage.removeItem(key);
        });
        results.cleanup = true;
      }

      onProgress?.('Migration complete', 100);

      return {
        success: true,
        message: 'Migration completed successfully',
        details: results
      };
    } catch (error: any) {
      return {
        success: false,
        message: `Migration failed: ${error.message}`,
        details: { error: error.message }
      };
    }
  }

  /**
   * Clear all data (for testing/reset)
   */
  static async clearAllData(): Promise<void> {
    // Clear IndexedDB
    const databases = await indexedDB.databases();
    for (const db of databases) {
      if (db.name) {
        await indexedDB.deleteDatabase(db.name);
      }
    }

    // Clear localStorage
    localStorage.clear();

    // Clear sessionStorage
    sessionStorage.clear();

    console.log('All data cleared');
  }
}